import clsx from "clsx";
import { useEffect, useRef } from "react";
import { CheckCheck } from "lucide-react";
import { useDispatch } from "react-redux";
import he from "he";

import useUserInfo from "@/hooks/use-user-info";
import useCsrTeam from "@/hooks/use-csr-team";
import type { ChatMessage } from "@/model/csr-chat";
import { MessageType } from "@/model/csr-chat";
import {
  CSR_BOT_REPLY_NO,
  CSR_BOT_REPLY_YES,
  CSR_CHANNEL_STATUS_CLOSED,
  CSR_ROLE_BOT,
  CSR_ROLE_MEMBER,
} from "@/services/csr-chat/csr-chat-constants";
import { emitChannelStatusChange, emitNewMessage } from "@/services/csr-chat";
import { addMessage } from "@/store/csr-chat/csr-chat-slice";
import { Button } from "@/components/ui/button";
import ProfileImage from "./ProfileImage";
import { AttachmentMessage, parseAttachmentProps } from "./AttachmentMessage";

export type BubblePosition = "single" | "first" | "middle" | "last";

type MessageBubbleProps = {
  message: ChatMessage;
  position?: BubblePosition;
  onVisible?: () => void;
  isSeen?: boolean;
  showTick?: boolean;
  showBotReply?: boolean;
  onToggleSeen?: () => void;
};

type BotReplyButtonsProps = {
  onReply: (text: string, closeChannel?: boolean) => void;
};

function getBubbleRadius(
  position: BubblePosition | undefined,
  isIncoming: boolean,
): string {
  if (isIncoming) {
    switch (position) {
      case "single":
        return "rounded-2xl rounded-tr-none";
      case "first":
        return "rounded-2xl rounded-br-md";
      case "middle":
        return "rounded-2xl rounded-tr-md rounded-br-md";
      case "last":
        return "rounded-2xl rounded-tr-md";
    }
  } else {
    switch (position) {
      case "single":
        return "rounded-2xl rounded-tl-none";
      case "first":
        return "rounded-2xl rounded-bl-md";
      case "middle":
        return "rounded-2xl rounded-tl-md rounded-bl-md";
      case "last":
        return "rounded-2xl rounded-tl-md";
    }
  }
}

function getRowMargin(position: BubblePosition): string {
  switch (position) {
    case "single":
      return "mt-2 mb-2";
    case "first":
      return "mt-2 mb-0.5";
    case "middle":
      return "my-0.5";
    case "last":
      return "mt-0.5 mb-2";
  }
}

const BotReplyButtons: React.FC<BotReplyButtonsProps> = ({ onReply }) => (
  <div className="mx-auto flex w-1/2 justify-between gap-2 p-5 pb-0">
    <Button
      variant="outline"
      className="flex-1"
      onClick={() => onReply(CSR_BOT_REPLY_YES)}
    >
      {CSR_BOT_REPLY_YES}
    </Button>
    <Button
      variant="outline"
      className="flex-1"
      onClick={() => onReply(CSR_BOT_REPLY_NO, true)}
    >
      {CSR_BOT_REPLY_NO}
    </Button>
  </div>
);

export const MessageBubble = ({
  message,
  position = "single",
  onVisible,
  isSeen = false,
  showTick = false,
  showBotReply = false,
  onToggleSeen,
}: MessageBubbleProps) => {
  const userInfo = useUserInfo();
  const dispatch = useDispatch();
  const { csrInitials } = useCsrTeam();

  const isIncoming = message.role !== CSR_ROLE_MEMBER;
  const isSeenByRecipient = !isIncoming && (message.receiver?.length ?? 0) > 0;
  const memberNameLetters = `${userInfo.firstname?.[0] ?? ""}${userInfo.lastname?.[0] ?? ""}`;

  const showAvatar = position === "single" || position === "first";
  const showTimestamp = position === "single" || position === "first";
  const timestamp = new Date(message.createdAt).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = new Date(message.createdAt).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const rowRef = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!onVisible || isSeen || firedRef.current) return;
    const el = rowRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          onVisible();
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [onVisible, isSeen]);

  const handleBotReply = (text: string, closeChannel = false) => {
    const tempId = crypto.randomUUID();
    emitNewMessage({
      message: text,
      chatChannel: message.chatChannel,
      tempId,
      type: MessageType.MESSAGE,
    });
    dispatch(
      addMessage({
        _id: tempId,
        chatChannel: message.chatChannel,
        createdAt: new Date().toISOString(),
        username: userInfo.username,
        role: CSR_ROLE_MEMBER,
        message: text,
        deleted: false,
      }),
    );
    if (closeChannel) {
      setTimeout(() => {
        emitChannelStatusChange({
          chatChannel: message.chatChannel,
          status: CSR_CHANNEL_STATUS_CLOSED,
        });
      }, 1500);
    }
  };

  if (message.role === CSR_ROLE_BOT && message.type === MessageType.INFO) {
    return (
      <div className="my-2 flex justify-center">
        <span
          className="border border-gray-300 bg-white px-4 py-1.5 text-xs text-gray-500"
          style={{ borderRadius: "33.33px" }}
        >
          {message.message}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={rowRef}
      className={clsx(
        "flex items-end gap-2",
        getRowMargin(position),
        !isIncoming && "flex-row-reverse",
      )}
    >
      <div className="size-10 flex-shrink-0">
        {showAvatar && (
          <ProfileImage
            nameLetters={isIncoming ? csrInitials : memberNameLetters}
          />
        )}
      </div>

      {/*
        2.1.1 Keyboard — A / 4.1.2 Name, Role, Value — A. This was a <div onClick>, so the
        show-timestamp toggle on a chat message could not be reached or activated by
        keyboard. It only becomes a control when onToggleSeen is supplied; otherwise it
        stays a plain container so no phantom tab stop is introduced.
      */}
      <div
        role={onToggleSeen ? "button" : undefined}
        tabIndex={onToggleSeen ? 0 : undefined}
        aria-label={onToggleSeen ? "Show message details" : undefined}
        onKeyDown={
          onToggleSeen
            ? (event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                onToggleSeen();
              }
            : undefined
        }
        className={clsx(
          "flex max-w-[60%] flex-col",
          !isIncoming && "items-end",
          onToggleSeen &&
            "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        )}
        onClick={onToggleSeen}
      >
        {showTimestamp && (
          <span className="mb-1 text-xs text-gray-400">{timestamp}</span>
        )}
        {message.type === MessageType.FILE ? (
          <AttachmentMessage
            {...parseAttachmentProps(message.message, message.chatChannel)}
          />
        ) : (
          <div
            className={clsx(
              "w-fit whitespace-pre-wrap break-words px-3 py-2 text-sm",
              getBubbleRadius(position, !isIncoming),
              "bg-gray-100 text-gray-800",
            )}
          >
            {he.decode(message.message)}
            {showBotReply && <BotReplyButtons onReply={handleBotReply} />}
          </div>
        )}
        {isIncoming && showTick && (
          <span className="mt-0.5 text-xs text-gray-400">{time}</span>
        )}
        {!isIncoming && showTick && (
          <div className="mt-0.5 flex items-center gap-1">
            <span className="text-xs text-gray-400">{time}</span>
            <CheckCheck
              size={14}
              className={clsx(
                isSeenByRecipient ? "text-blue-500" : "text-gray-400",
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
